import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type {
  UseQueryOptions,
  UseQueryResult,
  QueryKey,
} from '@tanstack/react-query';
import service from '../service';
import type { User } from '@Types/user';
import { stableStringify } from '@Utils/searchParams';

type UsersResponse = { data: User[]; meta: Record<string, any> };
type DefaultError = unknown;

type UseUsersParams<S extends Record<string, any> = Record<string, any>> = {
  /** Optional query string params to forward to service.getUsers */
  searchParams?: S;
  /** Override default enabled behavior */
  enabled?: boolean;
};

export function useUsers<TData = UsersResponse, TError = DefaultError>(
  { searchParams, enabled = true }: UseUsersParams = {},
  options?: Omit<
    UseQueryOptions<UsersResponse, TError, TData, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
): UseQueryResult<TData, TError> {
  const stableParams = useMemo(
    () => stableStringify(searchParams),
    [searchParams],
  );

  return useQuery<UsersResponse, TError, TData>({
    queryKey: ['users', stableParams],
    enabled: enabled,
    queryFn: async () => {
      const response = await service.getUsers(searchParams);
      return response;
    },
    ...(options as any),
  });
}
